import styled, { css } from 'styled-components';
import tw from 'twin.macro';
import { ICar, CarStatus } from '../../../../__generated__/graphql';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faStar, faEllipsisH } from '@fortawesome/free-solid-svg-icons';
import { URLS } from '../../../../constants/urls';
import { isWithinInterval, compareAsc } from 'date-fns';
import { formatInstantInZone } from '../../../helpers/timezoneHelpers';
import { useQuery } from '@apollo/client';
import { CAR_LISTING_STATUS } from '../../../services/listingHoldService/queries';
import { ICarListingStatusQuery } from '../../../services/listingHoldService/__generated__/queries.generated';
import { hostCarBadge } from '../../../utils/listingHold';
import { getBrand } from '../../../constants/brands';
import { Badge as UiBadge } from '../../../components/ui';

const CardContainer = styled.div<{ $isDeactivated?: boolean }>`
  ${tw`flex flex-col rounded-card overflow-hidden cursor-pointer`}
  box-shadow: var(--shadow-card);
  transition:
    box-shadow var(--transition-normal),
    transform var(--transition-normal),
    border-color var(--transition-normal);

  ${({ $isDeactivated }) =>
    $isDeactivated
      ? css`
          ${tw`bg-red-50 border border-red-200`}
        `
      : css`
          ${tw`bg-white border border-border-default`}
        `}

  &:hover {
    box-shadow: var(--shadow-card-hover);
    transform: translateY(-2px);
    border-color: var(--color-border-strong);
  }
`;

const ImageContainer = styled.div<{ $paused?: boolean }>`
  ${tw`relative w-full h-48 bg-bg-secondary`};
  ${({ $paused }) =>
    $paused &&
    css`
      filter: grayscale(1);
      opacity: 0.55;
    `}
`;

/** D41: the card text is dimmed while the host account is paused; the badge stays readable. */
const Dimmed = styled.div<{ $paused?: boolean }>`
  ${({ $paused }) =>
    $paused &&
    css`
      filter: grayscale(1);
      opacity: 0.6;
    `}
`;

const CarImage = styled.img<{ $isDeactivated?: boolean }>`
  ${tw`w-full h-full object-cover`}
  ${({ $isDeactivated }) => $isDeactivated && tw`opacity-60`}
`;

const Badge = styled.div<{ $isDeactivated?: boolean }>`
  ${tw`absolute top-3 left-3 text-xs font-semibold px-2.5 h-6 inline-flex items-center gap-1.5 rounded-full`}
  backdrop-filter: blur(4px);
  ${({ $isDeactivated }) =>
    $isDeactivated
      ? css`
          background-color: rgb(254 242 242 / 0.95);
          color: #b91c1c;
        `
      : css`
          background-color: rgb(255 255 255 / 0.92);
          color: #047857;
        `}
  &::before {
    content: '';
    ${tw`w-1.5 h-1.5 rounded-full inline-block`}
    background-color: currentColor;
  }
`;

const MenuButton = styled.button`
  ${tw`absolute top-3 right-3 bg-white w-8 h-8 flex items-center justify-center rounded-full shadow-card text-text-secondary border-none cursor-pointer`}

  &:hover {
    ${tw`bg-bg-secondary`}
  }
`;

const ContentContainer = styled.div`
  ${tw`flex flex-col p-4 space-y-1`}
`;

const Title = styled.h3`
  ${tw`text-base font-semibold text-text-primary truncate`}
`;

const Subtitle = styled.div`
  ${tw`text-sm text-text-secondary truncate`}
`;

const Footer = styled.div`
  ${tw`flex items-center justify-between text-xs mt-4 pt-3 border-t border-border-default`}
`;

const StatusText = styled.div`
  ${tw`text-text-secondary font-medium truncate max-w-[60%]`}
`;

const RatingContainer = styled.div`
  ${tw`flex items-center text-text-primary font-bold w-full max-w-full min-w-0 box-border`}
`;

const StarIcon = styled(FontAwesomeIcon)`
  ${tw`text-text-primary ml-1 w-3 h-3`}
`;

const TripCount = styled.span`
  ${tw`text-text-secondary font-normal ml-1`}
`;
const NoRatingText = styled.span`
  ${tw`text-text-secondary font-normal`}
`;

interface HostCarCardProps {
  car: ICar;
  onClick: () => void;
  onMenuClick?: (e: React.MouseEvent) => void;
  /** D41: set while the host account is paused or not active ("Paused", "Not active"). */
  pausedLabel?: string;
}

export function HostCarCard({ car, onClick, onMenuClick, pausedLabel }: HostCarCardProps) {
  const paused = Boolean(pausedLabel);
  const isDeactivated = car.status === CarStatus.Deactivated || car.status === CarStatus.Sold;
  // Spec 015: say why an unlisted car is unlisted (safety recall, the brand, sold).
  const { data: listingData } = useQuery<ICarListingStatusQuery>(CAR_LISTING_STATUS, {
    variables: { carId: car.id },
    skip: !isDeactivated || !car.id,
    fetchPolicy: 'cache-first',
  });
  const badgeText =
    hostCarBadge(car.status, listingData?.carListingStatus.hold?.reason, getBrand().name) ??
    'Listed';

  // Format trip info
  const getTripInfo = () => {
    if (!car.bookings || car.bookings.length === 0) return 'No trips yet';

    const tz = car.location?.timezone ?? 'America/Los_Angeles';
    const fmt = (ms: number, p: string) => formatInstantInZone(ms, tz, p);
    const now = new Date();

    const sortedBookings = [...car.bookings].sort((a: any, b: any) =>
      compareAsc(new Date(a.startAt), new Date(b.startAt)),
    );

    const activeBooking = sortedBookings.find((b: any) =>
      isWithinInterval(now, { start: new Date(b.startAt), end: new Date(b.endAt) }),
    );

    if (activeBooking) {
      return `On a trip: ${fmt(activeBooking.startAt, 'MMM d')} - ${fmt(activeBooking.endAt, 'MMM d')}`;
    }

    const nextBooking = sortedBookings.find((b: any) => new Date(b.startAt) > now);
    if (nextBooking) {
      return `Next trip: ${fmt(nextBooking.startAt, 'MMM d')} - ${fmt(nextBooking.endAt, 'MMM d')}`;
    }

    const pastBookings = sortedBookings.filter((b: any) => new Date(b.endAt) < now);
    if (pastBookings.length > 0) {
      const lastBooking = pastBookings[pastBookings.length - 1];
      return `Last trip: ${fmt(lastBooking.startAt, 'MMM d')} - ${fmt(lastBooking.endAt, 'MMM d')}`;
    }

    return 'No upcoming trips';
  };

  const tripInfo = getTripInfo();

  // Trip count logic
  const tripCount = car.bookings?.length || 0;

  // Mock rating logic
  const hasRatings = tripCount > 0;
  const rating = 5.0;

  return (
    <CardContainer
      onClick={onClick}
      $isDeactivated={isDeactivated}
      data-testid={paused ? 'host-car-card-paused' : undefined}
    >
      <ImageContainer $paused={paused}>
        <CarImage
          src={car.thumbnailUrl ? `${URLS.S3_BUCKET.CAR_THUMBNAILS}/${car.thumbnailUrl}` : ''}
          alt={car.name || 'Car'}
          $isDeactivated={isDeactivated}
        />
        <Badge $isDeactivated={isDeactivated} data-testid="host-car-status-badge">
          {badgeText}
        </Badge>
        <MenuButton
          onClick={e => {
            e.stopPropagation();
            onMenuClick && onMenuClick(e);
          }}
        >
          <FontAwesomeIcon icon={faEllipsisH} />
        </MenuButton>
      </ImageContainer>

      <ContentContainer>
        {paused && (
          <div>
            <UiBadge tone="warning" size="sm" dot data-testid="host-car-paused-badge">
              {pausedLabel}
            </UiBadge>
          </div>
        )}
        <Dimmed $paused={paused}>
          <Title>
            {car.make} {car.model} {car.year}
          </Title>
          <Subtitle>Base • {car.licensePlate}</Subtitle>
        </Dimmed>

        <Dimmed $paused={paused}>
          <Footer>
            <StatusText>{tripInfo}</StatusText>
            <RatingContainer>
              {hasRatings ? (
                <>
                  {rating.toFixed(2)} <StarIcon icon={faStar} />{' '}
                  <TripCount>({tripCount} trips)</TripCount>
                </>
              ) : (
                <NoRatingText>No ratings</NoRatingText>
              )}
            </RatingContainer>
          </Footer>
        </Dimmed>
      </ContentContainer>
    </CardContainer>
  );
}
